import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { afterLoginPath, useAuth } from "@/context/AuthContext";
import { PublicLayout } from "@/components/brand/Brand";
import { Field, Loader } from "@/components/common";
import { AuthCard } from "@/pages/SignIn";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <PublicLayout>
      <AuthCard title="Forgot password" subtitle="We'll email you a link to choose a new one. It works for 1 hour.">
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <Field label="Email" htmlFor="fp-email">
            <input id="fp-email" type="email" className="q-input" value={email} onChange={(e) => { setEmail(e.target.value); setMsg(""); }} data-testid="forgot-email-input" />
          </Field>
          {msg && <p className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-200" data-testid="forgot-message">{msg}</p>}
          {error && <p className="q-error" data-testid="forgot-error">{error}</p>}
          <button className="q-btn q-btn-primary w-full" disabled={busy || !email} data-testid="forgot-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link</button>
        </form>
        <Link to="/signin" className="mt-6 inline-block text-sm text-q-accent hover:underline" data-testid="forgot-back-link">Back to sign in</Link>
      </AuthCard>
    </PublicLayout>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`).then(({ data }) => setState(data)).catch((e) => setState({ invalid: errMsg(e) }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setError("Password must be at least 8 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      nav(afterLoginPath(signIn(data)), { replace: true });
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <PublicLayout>
      <AuthCard title="Choose a new password">
        {!state ? <Loader /> : state.invalid ? (
          <div data-testid="reset-invalid">
            <p className="font-semibold text-q-text" data-testid="reset-error">{state.invalid}</p>
            <Link to="/forgot-password" className="q-btn q-btn-secondary mt-5" data-testid="reset-request-new-link">Get a new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" noValidate data-testid="reset-form">
            <p className="text-sm text-q-muted">For {state.email}</p>
            <Field label="New password" htmlFor="rp-pass" error={error} errorTestid="reset-error">
              <input id="rp-pass" type="password" className="q-input" placeholder="8+ characters" value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} data-testid="reset-password-input" />
            </Field>
            <button className="q-btn q-btn-primary w-full" disabled={busy} data-testid="reset-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save and sign in</button>
          </form>
        )}
      </AuthCard>
    </PublicLayout>
  );
}
